import { Injectable, inject } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { createEffectFactory } from '@core/ngrx-legacy';

import { TrackingActivitiesHttpClient } from './tracking-activities-http.service';
import { TrackingActivitiesActions } from './tracking-activities.store';
import { ActivitiesDefaultLimit } from '../../types';

@Injectable({ providedIn: 'root' })
export class TrackingActivitiesEffects {
  private readonly trackingActivitiesService = inject(TrackingActivitiesHttpClient);
  private readonly actions$ = inject(Actions);

  public effects = createEffectFactory(TrackingActivitiesActions, this.actions$);

  public fetchCollection$ = this.effects(TrackingActivitiesActions.fetchCollection, ({ payload }) =>
    this.trackingActivitiesService.fetchTrackingActivities(payload.params).pipe(
      map(res => ({
        data: res,
        scope: payload.scope!,
        replace: payload.replace!,
        hasMore:
          res.activities.length >= (payload.params?.filters?.limit ?? ActivitiesDefaultLimit),
      })),
    ),
  );

  public fetchCandidateTrackingActivities$ = this.effects(
    TrackingActivitiesActions.fetchCandidateTrackingActivities,
    ({ payload }) =>
      this.trackingActivitiesService.fetchCandidateTrackingActivities(payload.params).pipe(
        map(res =>
          TrackingActivitiesActions.fetchCandidateTrackingActivitiesSuccess({
            payload: {
              data: res,
              scope: payload.scope!,
              replace: payload.replace!,
              loadMore: payload.loadMore!,
              hasMore:
                res.activities.length >= (payload.params?.filters?.limit ?? ActivitiesDefaultLimit),
            },
          }),
        ),
        catchError(error =>
          of(TrackingActivitiesActions.fetchCandidateTrackingActivitiesFailed({ payload: error })),
        ),
      ),
    { customMapOperator: mergeMap },
  );

  public fetchOfferTrackingActivities$ = this.effects(
    TrackingActivitiesActions.fetchOfferTrackingActivities,
    ({ payload }) =>
      this.trackingActivitiesService.fetchOfferTrackingActivities(payload.params).pipe(
        map(res =>
          TrackingActivitiesActions.fetchOfferTrackingActivitiesSuccess({
            payload: {
              data: res,
              scope: payload.scope!,
              replace: payload.replace!,
              hasMore:
                res.activities.length >= (payload.params?.filters?.limit ?? ActivitiesDefaultLimit),
            },
          }),
        ),
        catchError(error =>
          of(TrackingActivitiesActions.fetchOfferTrackingActivitiesFailed({ payload: error })),
        ),
      ),
    { customMapOperator: mergeMap },
  );

  public fetchTalentPoolTrackingActivities$ = this.effects(
    TrackingActivitiesActions.fetchTalentPoolTrackingActivities,
    ({ payload }) =>
      this.trackingActivitiesService.fetchTalentPoolTrackingActivities(payload.params).pipe(
        map(res =>
          TrackingActivitiesActions.fetchTalentPoolTrackingActivitiesSuccess({
            payload: {
              data: res,
              scope: payload.scope!,
              replace: payload.replace!,
              hasMore:
                res.activities.length >= (payload.params?.filters?.limit ?? ActivitiesDefaultLimit),
            },
          }),
        ),
        catchError(error =>
          of(TrackingActivitiesActions.fetchTalentPoolTrackingActivitiesFailed({ payload: error })),
        ),
      ),
  );

  public fetchRequisitionTrackingActivities$ = this.effects(
    TrackingActivitiesActions.fetchRequisitionTrackingActivities,
    ({ payload }) =>
      this.trackingActivitiesService.fetchRequisitionTrackingActivities(payload.params).pipe(
        map(res =>
          TrackingActivitiesActions.fetchRequisitionTrackingActivitiesSuccess({
            payload: {
              data: res,
              scope: payload.scope,
              replace: payload.replace!,
              hasMore:
                res.activities.length >= (payload.params?.filters?.limit ?? ActivitiesDefaultLimit),
            },
          }),
        ),
        catchError(error =>
          of(
            TrackingActivitiesActions.fetchRequisitionTrackingActivitiesFailed({ payload: error }),
          ),
        ),
      ),
  );
}
